import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    UntypedFormGroup
} from '@angular/forms';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Selection } from 'src/app/domain/models/selection';

@Component({
    selector: 'app-multi-selection-control',
    templateUrl: './multi-selection-control.component.html',
    styleUrls: ['./multi-selection-control.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: MultiSelectionControlComponent,
            multi: true
        }
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatCheckbox, MatFormFieldModule, FlexLayoutModule]
})
export class MultiSelectionControlComponent implements ControlValueAccessor {
    @Input() formGroup: UntypedFormGroup | undefined;
    @Input() selections: Selection[] = [];
    @Input() control: AbstractControl | null | undefined;
    @Input() formControlName: string | undefined;
    @Input() header: string | undefined;
    @Input() label: string | undefined;
    @Input() required = false;
    @Input() requiredMessage: string | undefined;
    @Input() requiredErrorKey: string | undefined;
    @Input() formSubmitted: boolean | undefined;

    public _value: number[] = [];
    readonly headerId = `selection-checkbox-header-${++MultiSelectionControlComponent.nextId}`;
    readonly labelId = `selection-checkbox-label-${MultiSelectionControlComponent.nextId}`;
    private static nextId = 0;
    private onChange: (_: number[]) => void = () => {};
    private onTouched: () => void = () => {};

    // ControlValueAccessor methods
    writeValue(value: number[]): void {
        this._value = value || [];
    }

    registerOnChange(fn: (_: number[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        void isDisabled;
    }

    updateCheckboxGroup(event: MatCheckboxChange) {
        const selectionId = Number(event.source.value);
        const index = this._value.indexOf(selectionId);

        if (event.checked && index === -1) {
            this._value.push(selectionId);
        } else if (!event.checked && index > -1) {
            this._value.splice(index, 1);
        }

        this.onChange(this._value);
        this.onTouched();
    }

    get hasRequiredError(): boolean {
        if (this.requiredErrorKey) {
            return this.formGroup?.hasError(this.requiredErrorKey) ?? false;
        }

        return this.control?.hasError('required') ?? false;
    }

    get activeHeadingId(): string {
        return this.header ? this.headerId : this.labelId;
    }
}
